import { offset } from '@floating-ui/react-dom';
import type { DateTime } from 'luxon';
import {
  type KeyboardEvent,
  type ChangeEvent,
  createRef,
  useEffect,
  useState,
} from 'react';
import type ReactDatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useTimeZonedDateUtils } from '@/hooks/useTimeZonedDateUtils';
import { DatePicker } from '@/pages/Schedule/components/DatePicker';
import { Checkbox, Input, Text, forwardRef } from '@/ui';
import { maybeAdjustDefaultYear } from '@/utils/dates';

type Props = {
  hasRecurrence: boolean;
  isOnDay: boolean;
  startDate: DateTime;
  endDate: DateTime;
  untilDate: DateTime | null;
  onStartChange: (startDate: DateTime) => void;
  onEndChange: (endDate: DateTime) => void;
  onUntilChange: (untilDate: DateTime | null) => void;
};

const CalendarInput = forwardRef((props, ref) => (
  <Input ref={ref} variant="notion" w="125px" {...props} />
));

export const DateRangeInput = ({
  hasRecurrence,
  isOnDay,
  startDate,
  endDate,
  untilDate,
  onStartChange,
  onEndChange,
  onUntilChange,
}: Props) => {
  const { t } = useTranslation('entryModal');
  const startDatePickerRef = createRef<ReactDatePicker>();
  const endDatePickerRef = createRef<ReactDatePicker>();
  const [isMouseOverCalendar, setIsMouseOverCalendar] = useState(false);
  const adjustedEndDate = endDate.minus({ days: isOnDay ? 1 : 0 });
  const { fromJSDate } = useTimeZonedDateUtils();

  const [indefinitely, setIndefinitely] = useState(hasRecurrence && !untilDate);
  useEffect(() => {
    setIndefinitely(hasRecurrence && !untilDate);
  }, [hasRecurrence, untilDate]);

  // The selected end date is either the endDate (non-recurring) or untilDate
  // (recurring with indefinitely deselected) or undefined (recurring with
  // indefinitely selected).
  let selectedEndDate: DateTime | null = null;
  if (hasRecurrence && !indefinitely) {
    selectedEndDate = untilDate;
  } else if (!hasRecurrence) {
    selectedEndDate = adjustedEndDate;
  }

  const handleStartChange = (date: Date | null) => {
    if (!date) return;
    const newStartDate = maybeAdjustDefaultYear(fromJSDate(date));
    onStartChange(newStartDate);
  };

  const handleEndChange = (date: Date | null) => {
    if (!date) return;
    const newEndDate = maybeAdjustDefaultYear(
      fromJSDate(date).plus({
        days: isOnDay ? 1 : 0,
      })
    );
    onEndChange(newEndDate);
  };

  const handleUntilChange = (date: Date | null) => {
    setIndefinitely(false);
    onUntilChange(date ? fromJSDate(date).endOf('day') : null);
  };

  const handleIndefinitelyChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    setIndefinitely(checked);
    if (checked) {
      onUntilChange(null);
      endDatePickerRef.current?.setOpen(false);
    }
  };

  // Move focus to the next input on tab instead of entering the calendar
  //github.com/Hacker0x01/react-datepicker/issues/2058#issuecomment-589451843
  const handleStartKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab') {
      startDatePickerRef.current?.setOpen(false);
    }
  };
  const handleEndKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab') {
      endDatePickerRef.current?.setOpen(false);
    }
  };

  return (
    <>
      <DatePicker
        dateFormat="MMM d, yyyy"
        popperModifiers={[offset({ crossAxis: -16 })]}
        ref={startDatePickerRef}
        selected={startDate}
        selectsStart={hasRecurrence}
        customInput={
          <CalendarInput
            aria-label={t('date.aria.start')}
            onChange={handleStartChange}
          />
        }
        onChange={handleStartChange}
        onKeyDown={handleStartKeyDown}
      />

      <Text color="customgray.alsolight">&ndash;</Text>

      <DatePicker
        dateFormat="MMM d, yyyy"
        endDate={hasRecurrence ? untilDate : adjustedEndDate}
        minDate={startDate}
        popperModifiers={[offset({ crossAxis: -16 })]}
        ref={endDatePickerRef}
        selected={selectedEndDate}
        selectsEnd
        showPopperArrow={false}
        value={hasRecurrence && !untilDate ? t('date.indefinitely') : undefined}
        customInput={
          <CalendarInput
            aria-label={
              hasRecurrence ? t('date.aria.until') : t('date.aria.end')
            }
            onBlur={() => endDatePickerRef.current?.setOpen(false)}
            onChange={hasRecurrence ? handleUntilChange : handleEndChange}
          />
        }
        startDate={
          !hasRecurrence || untilDate || isMouseOverCalendar
            ? startDate
            : undefined
        }
        onChange={hasRecurrence ? handleUntilChange : handleEndChange}
        onDayMouseEnter={() => setIsMouseOverCalendar(true)}
        onKeyDown={handleEndKeyDown}
        onMonthMouseLeave={() => setIsMouseOverCalendar(false)}
      >
        {hasRecurrence && (
          <Checkbox
            color="customgray.mid"
            isChecked={indefinitely}
            onChange={handleIndefinitelyChange}
          >
            {t('date.indefinitely')}
          </Checkbox>
        )}
      </DatePicker>
    </>
  );
};
